/**
 * Helpers for Outwrite learner practice (UI + to_finds.options contract).
 */

export function givenIdFromTaskId(taskId) {
  if (!taskId || typeof taskId !== 'string') return null;
  const idx = taskId.lastIndexOf('__');
  return idx > 0 ? taskId.slice(0, idx) : taskId;
}

export function taskTypeFromId(taskId) {
  if (!taskId || typeof taskId !== 'string') return null;
  if (taskId.endsWith('__pre_writing')) return 'pre_writing';
  if (taskId.endsWith('__overview')) return 'overview';
  return null;
}

/**
 * Group list items `{ id, difficulty }` by given_id for the landing picker.
 * @returns {{ givenId: string, difficulty: number, preWritingId?: string, overviewId?: string }[]}
 */
export function groupTasksByGivenId(items) {
  const map = new Map();
  for (const item of Array.isArray(items) ? items : []) {
    const givenId = givenIdFromTaskId(item.id);
    if (!givenId) continue;
    const type = taskTypeFromId(item.id);
    const row = map.get(givenId) || {
      givenId,
      difficulty: item.difficulty,
      preWritingId: undefined,
      overviewId: undefined,
    };
    if (item.difficulty != null) row.difficulty = item.difficulty;
    if (type === 'pre_writing') row.preWritingId = item.id;
    if (type === 'overview') row.overviewId = item.id;
    map.set(givenId, row);
  }
  return [...map.values()].sort((a, b) => {
    const d = (a.difficulty ?? 99) - (b.difficulty ?? 99);
    return d !== 0 ? d : a.givenId.localeCompare(b.givenId);
  });
}

export function seriesLabelsFromTask(task) {
  const series = task?.given?.data?.series;
  if (!series || typeof series !== 'object' || Array.isArray(series)) return [];
  return Object.keys(series);
}

/** Trend-style question: segmented UI per series (mockups 03/06). */
export function isTrendToFind(toFind) {
  if (!toFind) return false;
  if (toFind.id === 'q_line_trends' || toFind.key_id === 'series_end_trends') {
    return true;
  }
  const prompt = (toFind.prompt || '').toLowerCase();
  return (
    prompt.includes('upward') ||
    prompt.includes('downward') ||
    prompt.includes('beginning and the end of each line')
  );
}

export function keyValueForToFind(task, toFind) {
  const keyId = toFind?.key_id;
  if (!keyId || !Array.isArray(task?.keys)) return undefined;
  const row = task.keys.find((k) => k.id === keyId);
  return row?.value;
}

/**
 * Resolve explanation string for a to_find after check.
 * explanation may be a string or a map keyed by option / tense value.
 */
export function resolveExplanation(toFind, selectedValue, keyValue) {
  const expl = toFind?.explanation;
  if (expl == null || expl === '') return '';
  if (typeof expl === 'string') return expl;
  if (typeof expl === 'object') {
    const bySelected = expl[selectedValue];
    if (typeof bySelected === 'string' && bySelected) return bySelected;
    const byKey = expl[keyValue];
    if (typeof byKey === 'string' && byKey) return byKey;
  }
  return '';
}

/** Map key phrases like "upward" / "downward" to segmented values. */
function trendDirectionFromText(text) {
  const t = String(text || '').toLowerCase();
  if (/\b(upward|up|rise|increase|growth)\b/.test(t)) return 'up';
  if (/\b(downward|down|fall|decline|decrease)\b/.test(t)) return 'down';
  if (/\b(same|stable|unchanged|flat|level)\b/.test(t)) return 'same';
  return null;
}

/**
 * Parse series_end_trends key into { [seriesLabel]: 'up'|'down'|'same' }.
 * Supports "Visitors: upward", "CD: downward; Download: upward; …",
 * "all series: upward".
 */
export function parseTrendKeySelections(keyValue, seriesLabels) {
  const labels = Array.isArray(seriesLabels) ? seriesLabels : [];
  const map = {};
  if (keyValue == null || typeof keyValue !== 'string') return map;

  if (/all series/i.test(keyValue)) {
    const dir = trendDirectionFromText(keyValue);
    if (dir) labels.forEach((s) => { map[s] = dir; });
    return map;
  }

  for (const part of keyValue.split(';')) {
    const colon = part.indexOf(':');
    if (colon < 0) continue;
    const name = part.slice(0, colon).trim();
    const dir = trendDirectionFromText(part.slice(colon + 1));
    if (name && dir) map[name] = dir;
  }

  // Single-series shorthand: key names one series, or value is only a direction.
  if (!Object.keys(map).length && labels.length === 1) {
    const dir = trendDirectionFromText(keyValue);
    if (dir) map[labels[0]] = dir;
  }
  return map;
}

/**
 * Local check only (keys may be on GET this iteration).
 * @returns {{
 *   correct: boolean,
 *   explanation: string,
 *   correctValue?: string,
 *   correctSelections?: Record<string, string>,
 * }}
 */
export function checkPreWritingAnswer(task, toFind, answer) {
  const keyValue = keyValueForToFind(task, toFind);
  if (answer?.type === 'option') {
    const selected = answer.value;
    if (keyValue == null) {
      return {
        correct: true,
        correctValue: selected,
        explanation: resolveExplanation(toFind, selected, keyValue),
      };
    }
    const correct = selected === keyValue;
    return {
      correct,
      correctValue: keyValue,
      explanation: resolveExplanation(toFind, selected, keyValue),
    };
  }
  if (answer?.type === 'segmented') {
    const labels = seriesLabelsFromTask(task);
    const correctSelections = parseTrendKeySelections(keyValue, labels);
    const selected = answer.selections || {};
    const hasKey = Object.keys(correctSelections).length > 0;
    let correct = true;
    if (hasKey) {
      correct = labels.every(
        (s) => selected[s] && selected[s] === correctSelections[s],
      );
    }
    return {
      correct,
      correctSelections: hasKey ? correctSelections : { ...selected },
      explanation: resolveExplanation(toFind, null, keyValue),
    };
  }
  return { correct: false, explanation: '' };
}

/**
 * Lexis chips for words panel / summary from overview keys (when present).
 * @returns {{ quantity: string[], series: { name: string, words: string[] }[] }}
 */
export function extractPracticeLexis(overviewTask) {
  const keys = Array.isArray(overviewTask?.keys) ? overviewTask.keys : [];
  const quantityRow = keys.find((k) => k.id === 'lexis_quantity');
  const quantity = Array.isArray(quantityRow?.value) ? quantityRow.value : [];

  const featuresRow = keys.find((k) => k.id === 'chart_features');
  const features = Array.isArray(featuresRow?.value) ? featuresRow.value : [];
  const bySeries = new Map();
  for (const f of features) {
    if (!f?.series || !Array.isArray(f.lexis) || !f.lexis.length) continue;
    const prev = bySeries.get(f.series) || [];
    const merged = [...prev];
    for (const w of f.lexis) {
      if (w && !merged.includes(w)) merged.push(w);
    }
    bySeries.set(f.series, merged);
  }
  return {
    quantity,
    series: [...bySeries.entries()].map(([name, words]) => ({ name, words })),
  };
}

export function countSentences(text) {
  const t = (text || '').trim();
  if (!t) return 0;
  const parts = t.split(/[.!?]+/).map((s) => s.trim()).filter(Boolean);
  return parts.length;
}

export function countWords(text) {
  const t = (text || '').trim();
  if (!t) return 0;
  return t.split(/\s+/).filter(Boolean).length;
}

const STORAGE_PREFIX = 'outwrite-learner:';

export function loadSessionState(givenId) {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + givenId);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveSessionState(givenId, state) {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + givenId, JSON.stringify(state));
  } catch {
    /* ignore quota */
  }
}
